import { useSyncExternalStore } from "react";

let hideOverlayDepth = 0;
let hideOverlayPrev: {
    htmlBg: string;
    bodyBg: string;
    mainVisibility: string;
    footerVisibility: string;
} | null = null;

export function useHideAppBehindOverlay(open: boolean) {
    useSyncExternalStore(
        () => {
            if (!open) return () => {};
            const html = document.documentElement;
            const { body } = document;
            const main = document.getElementById("main-content");
            const footer = document.getElementById("site-footer");
            const isFirstOverlay = hideOverlayDepth === 0;
            hideOverlayDepth += 1;
            if (isFirstOverlay) {
                hideOverlayPrev = {
                    htmlBg: html.style.backgroundColor,
                    bodyBg: body.style.backgroundColor,
                    mainVisibility: main?.style.visibility ?? "",
                    footerVisibility: footer?.style.visibility ?? "",
                };
                html.style.backgroundColor = "var(--true-white)";
                body.style.backgroundColor = "var(--true-white)";
                if (main) main.style.visibility = "hidden";
                if (footer) footer.style.visibility = "hidden";
            }
            return () => {
                hideOverlayDepth -= 1;
                if (hideOverlayDepth === 0 && hideOverlayPrev) {
                    html.style.backgroundColor = hideOverlayPrev.htmlBg;
                    body.style.backgroundColor = hideOverlayPrev.bodyBg;
                    if (main)
                        main.style.visibility = hideOverlayPrev.mainVisibility;
                    if (footer)
                        footer.style.visibility =
                            hideOverlayPrev.footerVisibility;
                    hideOverlayPrev = null;
                }
            };
        },
        () => open,
        () => false,
    );
}

export type OverlayViewportRect = {
    height: number;
    offsetTop: number;
};

let overlayViewportRect: OverlayViewportRect | undefined;
let overlayViewport: VisualViewport | null = null;
const overlayViewportListeners = new Set<() => void>();

function readOverlayViewportRect() {
    return overlayViewportRect;
}

function updateOverlayViewportRect() {
    if (!overlayViewport) return;
    const next = {
        height: overlayViewport.height,
        offsetTop: overlayViewport.offsetTop,
    };
    if (
        next.height === overlayViewportRect?.height &&
        next.offsetTop === overlayViewportRect.offsetTop
    )
        return;
    overlayViewportRect = next;
    for (const listener of overlayViewportListeners) listener();
}

function subscribeToOverlayViewport(listener: () => void) {
    const viewport = window.visualViewport;
    if (!viewport) return () => {};

    overlayViewportListeners.add(listener);
    if (overlayViewportListeners.size === 1) {
        overlayViewport = viewport;
        updateOverlayViewportRect();
        viewport.addEventListener("resize", updateOverlayViewportRect);
        viewport.addEventListener("scroll", updateOverlayViewportRect);
    }

    return () => {
        overlayViewportListeners.delete(listener);
        if (overlayViewportListeners.size > 0) return;
        viewport.removeEventListener("resize", updateOverlayViewportRect);
        viewport.removeEventListener("scroll", updateOverlayViewportRect);
        overlayViewport = null;
        overlayViewportRect = undefined;
    };
}

const subscribeToNoViewport = () => () => {};

export function useOverlayViewportRect(
    active: boolean,
): OverlayViewportRect | undefined {
    return useSyncExternalStore(
        active ? subscribeToOverlayViewport : subscribeToNoViewport,
        () => (active ? readOverlayViewportRect() : undefined),
        () => undefined,
    );
}
